import { create } from 'zustand';
import { useOrderStore } from './orderStore';
import { useProductStore } from './productStore';

interface RefreshState {
  isRefreshing: boolean;
  lastRefresh: number;
  hardRefresh: () => Promise<void>;
}

export const getCacheBuster = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback valid UUID format if crypto is unavailable
    const hex = Date.now().toString(16).padStart(12, '0');
    return `00000000-0000-4000-8000-${hex.slice(-12)}`;
};

export const useRefreshStore = create<RefreshState>((set, get) => ({
  isRefreshing: false,
  lastRefresh: Date.now(),

  hardRefresh: async () => {
    const { isRefreshing, lastRefresh } = get();
    const now = Date.now();
    
    // Throttle to once every 20 seconds unless first time
    if (isRefreshing || (lastRefresh > 0 && now - lastRefresh < 20000)) {
        if (import.meta.env.DEV) console.log('[Refresh] Throttled. Last refresh was too recent.');
        return;
    }

    set({ isRefreshing: true, lastRefresh: now });

    try {
      // 1. Clear Service Worker Cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
        // Give it a tiny bit of time to clear
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // 2. Trigger global refetch by incrementing ticks in order store
      // The order store has realtimeUpdateTick which components already listen to
      const orderStore = useOrderStore.getState();
      useOrderStore.setState({ 
        realtimeUpdateTick: orderStore.realtimeUpdateTick + 1,
        // orders: [] // Removed: Do not clear existing to prevent flashing loaders
      });

      // 3. Trigger product store refresh
      const productStore = useProductStore.getState();
      await productStore.fetchProducts({ append: false });

      // 4. Update last refresh timestamp
      set({ lastRefresh: Date.now() });
      if (import.meta.env.DEV) console.log('[Refresh] Power Refresh complete.');
    } catch (error) {
      console.error('[Refresh] Power Refresh failed:', error);
    } finally {
      // Delay finishing the animation to make it feel "powerful"
      setTimeout(() => set({ isRefreshing: false }), 500);
    }
  },
}));
